import React, { useState, useEffect } from "react";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import { useOutletContext } from "react-router-dom";
import { getGuideSetting, updateGuideSettings } from "../api/user";
import { toast } from "react-toastify";

const Guide = () => {
  const [guideIcon, setGuideIcon] = useState(false);
  const [oneTimeGuide, setOneTimeGuide] = useState(false);

  const [sidebarToggle] = useOutletContext();
  const isMobileView = document.body.clientWidth <= 768;

  // -------------------------
  // GUIDE ICON TOGGLE
  // -------------------------
  const handleGuideToggle = async () => {
    const newStatus = !guideIcon;
    setGuideIcon(newStatus);

    try {
      const payload = {
        show_icon: newStatus,
        isMobileView: isMobileView,
      };

      await updateGuideSettings(payload);
      toast.success("Page tips updated successfully!");
    } catch (error) {
      console.log("Update failed:", error);
      toast.error("Could not save Page tips. Try again.");
    }
  };

  // -------------------------
  // ONE TIME GUIDE TOGGLE
  // -------------------------
  const handleOneTimeToggle = async () => {
    const newStatus = !oneTimeGuide;
    setOneTimeGuide(newStatus);

    try {
      const payload = {
        one_time: newStatus,
        isMobileView: isMobileView,
      };

      await updateGuideSettings(payload);
      toast.success("Page tips updated successfully!");
    } catch (error) {
      console.log("Update failed:", error);
      toast.error("Could not save Page tips. Try again.");
    }
  };

  // -------------------------
  // FETCH GUIDE SETTINGS
  // -------------------------
  useEffect(() => {
    const fetchGuideStatus = async () => {
      try {
        const response = await getGuideSetting();

        if (response.data.success && response.data.status === 200) {
          setGuideIcon(response.data.show_icon);

          // IMPORTANT ➜ Mobile uses mobile_guide_shown
          // IMPORTANT ➜ Mobile uses mobile_guide_shown
          if (isMobileView) {
            setOneTimeGuide(!response.data.mobile_guide_shown);
          } else {
            setOneTimeGuide(!response.data.guide_shown);
          }

        }
      } catch (error) {
        console.log("Error fetching guide status:", error);
      }
    };

    fetchGuideStatus();
  }, []);

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {/* Settings sitewide: no desktop title/grey line, no mobile under-logo
          title — the in-body heading below is the single title. */}
      <PageHeaderTitle title={"Page tips"} showDesktopTitle={false} />

      <PageContainer>
        {/* Desktop: white card. Mobile: bare, full-width (no card/fill). Title +
            subtitle sit outside the card, aligned to the card's left edge. */}
        <div className="px-4 pt-4 md:pt-10 md:px-0 md:max-w-[55rem] md:mx-auto">
          <SettingsPageHeading
            title="Page tips"
            subtitle="Control the green info icons that explain each page."
            className="mb-3 md:mb-4"
          />
          <div className="md:rounded-2xl md:border md:border-gray-200 md:bg-white md:p-8">
            {/* Row 1 — Show info icons. Toggle is TOP-aligned with the label
                (items-start), not centered between the label and its sub-text. */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {/* Match the on-page info launcher EXACTLY (the green icon on
                    /feed etc.): a #35b23c circle with a white info glyph inside.
                    That button is bg #35b23c + text-white + rounded-full with the
                    same fill="currentColor" SVG, so we mirror it here. */}
                <span
                  className="mt-0.5 shrink-0 inline-flex items-center justify-center rounded-full p-0.5 text-white"
                  style={{ backgroundColor: "#35b23c" }}
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <div>
                  <p className="font-medium text-gray-800">Show info icons</p>
                  <p className="mt-0.5 text-sm text-secondary">
                    Show or hide the green info icons on app pages.
                  </p>
                </div>
              </div>

              <div
                onClick={handleGuideToggle}
                className="w-14 h-7 rounded-full flex items-center cursor-pointer transition-all shrink-0"
                style={{
                  backgroundColor: guideIcon ? "#35b23c" : "#d3d3d3",
                  justifyContent: guideIcon ? "flex-end" : "flex-start",
                  padding: "3px",
                }}
              >
                <div className="w-6 h-6 bg-white rounded-full shadow" />
              </div>
            </div>

            {/* Row 2 — Show tips once. */}
            <div className="mt-6 flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-gray-800">Show tips once</p>
                <p className="mt-0.5 text-sm text-secondary">
                  Hide a page tip after you’ve viewed it.
                </p>
              </div>

              <div
                onClick={handleOneTimeToggle}
                className="w-14 h-7 rounded-full flex items-center cursor-pointer transition-all shrink-0"
                style={{
                  backgroundColor: oneTimeGuide ? "#35b23c" : "#d3d3d3",
                  justifyContent: oneTimeGuide ? "flex-end" : "flex-start",
                  padding: "3px",
                }}
              >
                <div className="w-6 h-6 bg-white rounded-full shadow" />
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default Guide;
